import React from 'react';
import { Package, CheckCircle, Globe, Shield, TrendingUp } from 'lucide-react';

export default function SupplyChain() {
  const framework = [
    {
      phase: 'Phase 1: Diagnostic Mapping',
      duration: 'Weeks 1-3',
      activities: [
        'Full-stack supply network mapping from suppliers to final mile',
        'Bottleneck, risk, and cost hotspot analysis using current data',
        'Working capital review across inventory, payables, and freight',
        'Scenario modeling for nearshoring, multi-node fulfillment, and automation'
      ]
    },
    {
      phase: 'Phase 2: Network Design',
      duration: 'Weeks 4-6',
      activities: [
        'Node strategy that balances service levels and landed cost',
        'Partner scorecards covering reliability, ESG requirements, and capacity',
        'Playbooks for compliance, labeling, and retailer routing guides',
        'Technology stack recommendations for visibility and planning'
      ]
    },
    {
      phase: 'Phase 3: Build & Launch',
      duration: 'Weeks 7-12',
      activities: [
        'Supplier negotiations, onboarding, and quality gates',
        '3PL / 4PL selection and contract support',
        'Process documentation for order flow, replenishment, and exception handling',
        'Control tower dashboards to monitor fulfillment, OTIF, and dwell time'
      ]
    },
    {
      phase: 'Phase 4: Continuous Optimization',
      duration: 'Ongoing',
      activities: [
        'Quarterly S&OP cadence with demand, inventory, and finance stakeholders',
        'Risk mitigation plans for geopolitical, compliance, and climate disruption',
        'Automation roadmap spanning EDI, labeling, and replenishment triggers',
        'Cost-to-serve analytics tying every improvement to gross margin'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Global Supplier Bench',
      icon: Globe,
      description: 'Pre-vetted factories and component partners across North America, LATAM, and APAC with proven compliance and startup-friendly MOQs.'
    },
    {
      title: 'Resilience by Design',
      icon: Shield,
      description: 'Dual-source strategies, buffer policies, and dynamic safety stock models keep orders shipping even when disruption hits.'
    },
    {
      title: 'Landed Cost Discipline',
      icon: TrendingUp,
      description: 'Every configuration is priced to the penny, factoring tariffs, duties, drayage, packaging, and accessorials so margin targets stay protected.'
    },
    {
      title: 'Execution Playbooks',
      icon: Package,
      description: 'Step-by-step SOPs for production handoff, QC, routing guides, and returns give operators clarity on day one.'
    }
  ];

  const deliverables = [
    'Network blueprint with recommended nodes, partners, and cost model',
    'Implementation roadmap covering people, process, and technology milestones',
    'Dashboard architecture with KPIs for service, cost, inventory, and risk',
    'Supplier and logistics partner roster with onboarding status and contacts'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Package size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Supply Chain Design & Setup</h1>
              <p className="text-xl text-gray-600 mt-2">Build a resilient, scalable fulfillment backbone</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Resilient Supply Chain Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Growth collapses when logistics break. We architect supply chains that flex with demand, absorb disruption, and stay profitable as volumes scale.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why Brands Choose Motif Unbound</h2>
          <p className="text-lg text-gray-700 mb-8">
            We are operators, not consultants. Every recommendation is grounded in real factory floors, freight lanes, and retailer scorecards.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What You Receive</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Engineer a Supply Chain That Keeps Up</h2>
          <p className="text-xl mb-8 opacity-90">
            We translate growth plans into dependable operations. Let’s design the backbone of your global commerce engine.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Start%20the%20Conversation&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Start the Conversation
          </a>
        </div>
      </div>
    </div>
  );
}
